import { DataFormats } from '@skylab-shared/constants';
import { HttpService, Query, QueryHandler, RootContextBase, TaggedUrl, Tags, transformDTODate } from '@skylab-shared/core';
import { PurchaseDocumentKind, SaleDocumentKind } from '@skylab-shared/dataModel';
import { MS_PER_MINUTE, StringUtils } from '@skylab-shared/utils';
import { Transform } from 'class-transformer';
import { format } from 'fecha';
import { CountryCollectionQuery } from '../../country/queries/country.query-base';
import { ArrearKind } from './arrear-kind';
import { PaymentSettlementKindInReport } from './payment-settlement-kind-in-report';

export interface UnsettledPaymentsParameters {
    readonly noClientGroup?: boolean;
    readonly search: string;
    readonly orderBy?: string[];

    arrearKind: ArrearKind;
    paymentKinds: PaymentSettlementKindInReport[];
    clientId?: number;
    clientGroupId?: string;
    issueDateFrom: Date;
    issueDateTo: Date;
    arrearValueFrom: number;
    arrearValueTo: number;
}

export interface UnsettledPaymentsModel {
    amountToPay: number;
    baseCurrencyId: string;
    baseCurrencyAmountToPay: number;
    baseCurrencyGross: number;
    baseCurrencyNet: number;
    baseCurrencyVat: number;
    client: string;
    clientAddress: string | null;
    clientCountry?: string;
    clientGroup: string;
    currencyId: string;
    gross: number;
    delay: number;
    documentKind: SaleDocumentKind | PurchaseDocumentKind;
    documentId: number;
    documentNumber: string;
    externalDocumentNumber: string;
    dueDate: Date;
    issueDate: Date;
    net: number;
    paymentSettlementKind: PaymentSettlementKindInReport;
    tin: string;
    vat: number;
}

class UnsettledPaymentsInternalItemDTO {
    public readonly amountToPay!: number;
    public readonly baseCurrencyAmountToPay!: number;
    public readonly baseCurrencyGross!: number;
    public readonly baseCurrencyId!: string;
    public readonly baseCurrencyNet!: number;
    public readonly baseCurrencyVat!: number;
    public readonly client!: string;
    public readonly clientAddress!: {
        address: string;
        city: string;
        countryId: number;
        name: string;
        zipCode: string;
    };
    public readonly clientGroup!: string;
    public readonly currencyId!: string;
    public readonly delay!: number;
    public readonly documentId!: number;
    public readonly documentKind!: SaleDocumentKind | PurchaseDocumentKind;
    public readonly documentNumber!: string;
    public readonly externalDocumentNumber!: string;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly dueDate!: Date;
    public readonly gross!: number;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly issueDate!: Date;
    public readonly net!: number;
    public readonly paymentSettlementKind!: PaymentSettlementKindInReport;
    public readonly tin!: string;
    public readonly vat!: number;
}

export class UnsettledPaymentsCollectionQuery extends Query<UnsettledPaymentsModel[]> {
    public constructor(public readonly parameters: UnsettledPaymentsParameters) {
        super();
    }
}

export class UnsettledPaymentsCollectionQueryHandlerBase implements QueryHandler<UnsettledPaymentsCollectionQuery, UnsettledPaymentsModel[]> {
    private static Url = TaggedUrl.join('unsettledPayment', [Tags.Api]);

    public constructor(
        private rootContext: RootContextBase,
        private readonly http: HttpService
    ) { }

    public async handleAsync(query: UnsettledPaymentsCollectionQuery): Promise<UnsettledPaymentsModel[]> {
        const { data } = await this.http.get<UnsettledPaymentsInternalItemDTO[]>(UnsettledPaymentsCollectionQueryHandlerBase.Url, {
            params: {
                ...query.parameters,
                issueDateFrom: query.parameters.issueDateFrom && format(query.parameters.issueDateFrom, DataFormats.date.webApi),
                issueDateTo: query.parameters.issueDateTo && format(query.parameters.issueDateTo, DataFormats.date.webApi)
            },
            timeout: 3 * MS_PER_MINUTE,
            dto: UnsettledPaymentsInternalItemDTO
        });

        const countries = await this.rootContext.getAsync(CountryCollectionQuery);

        return data.map(item => ({
            amountToPay: item.amountToPay,
            baseCurrencyId: item.baseCurrencyId,
            baseCurrencyAmountToPay: item.baseCurrencyAmountToPay,
            baseCurrencyGross: item.baseCurrencyGross,
            baseCurrencyNet: item.baseCurrencyNet,
            baseCurrencyVat: item.baseCurrencyVat,
            client: item.client,
            clientAddress: item.clientAddress
                ? StringUtils.join(', ', item.clientAddress.address, item.clientAddress.zipCode, item.clientAddress.city)
                : null,
            clientCountry: countries.find(elem => elem.id === item.clientAddress?.countryId)?.name,
            clientGroup: item.clientGroup,
            currencyId: item.currencyId,
            delay: item.delay,
            documentId: item.documentId,
            documentKind: item.documentKind,
            documentNumber: item.documentNumber,
            dueDate: item.dueDate,
            externalDocumentNumber: item.externalDocumentNumber,
            gross: item.gross,
            issueDate: item.issueDate,
            net: item.net,
            paymentSettlementKind: item.paymentSettlementKind,
            tin: item.tin,
            vat: item.vat,
        }));
    }
}
